import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Sidebar from '@/components/layout/Sidebar'
import Header from '@/components/layout/Header'
import CADWorkspace from '@/features/cad/CADWorkspace'
import PrintDashboard from '@/features/print/PrintDashboard'
import Marketplace from '@/features/marketplace/Marketplace'
import Dashboard from '@/features/dashboard/Dashboard'
import ProjectsView from '@/features/projects/ProjectsView'
import ProjectDetailsView from '@/features/projects/ProjectDetailsView'
import SettingsView from '@/features/settings/SettingsView'
import DesignUploadPage from '@/features/design/DesignUploadPage'
import PrinterManagement from '@/features/printer/PrinterManagement'
import AICopilot from '@/features/ai/AICopilot'
import TextTo3DPage from '@/features/ai/TextTo3DPage'
import { useAppStore } from '@/stores/appStore'
import type { Project } from '@/lib/database.types'

export type ViewType = 'dashboard' | 'design' | 'upload' | 'print' | 'marketplace' | 'projects' | 'project-details' | 'settings' | 'printers' | 'text-to-3d'

const HASH_TO_VIEW: Record<string, ViewType> = {
  '': 'dashboard',
  '#': 'dashboard',  
  '#dashboard': 'dashboard',
  '#projects': 'projects',
  '#design': 'design',
  '#printers': 'printers',
  '#text-to-3d': 'text-to-3d',
  '#print': 'print',
  '#marketplace': 'marketplace',
  '#settings': 'settings',
  '#upload': 'upload',
  '#project-details': 'project-details',
}

export default function DashboardApp() {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null)
  const [selectedDesignUrl, setSelectedDesignUrl] = useState<string | null>(null)
  const [currentHash, setCurrentHash] = useState(window.location.hash)
  const { isCopilotOpen, toggleCopilot } = useAppStore()

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => setCurrentHash(window.location.hash)
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // Get current view from hash
  const currentView: ViewType = HASH_TO_VIEW[currentHash] || 'dashboard'

  // Navigate to a view (updates hash)
  const navigateTo = useCallback((view: ViewType) => {
    window.location.hash = view === 'dashboard' ? '' : view
  }, [])

  const handleSelectProject = (project: Project) => {
    setSelectedProjectId(project.id)
    navigateTo('project-details')
  }

  const handleSelectDesign = (designId: string | null, designUrl: string | null) => {
    setSelectedDesignId(designId)
    setSelectedDesignUrl(designUrl)
    navigateTo('design')
  }

  const handleInsertModel = (code: string, _name: string) => {
    // Create a data URL from the SCAD code for the CAD workspace
    const scadDataUrl = `data:text/x-scad;base64,${btoa(code)}`
    setSelectedDesignId(null)
    setSelectedDesignUrl(scadDataUrl)
    navigateTo('design')
  }

  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard onNavigate={navigateTo} onSelectDesign={handleSelectDesign} />
      case 'projects':
        return <ProjectsView onSelectProject={handleSelectProject} />
      case 'project-details':
        return selectedProjectId ? (
          <ProjectDetailsView 
            projectId={selectedProjectId} 
            onBack={() => navigateTo('projects')} 
            onSelectDesign={handleSelectDesign}
          />
        ) : <ProjectsView onSelectProject={handleSelectProject} />
      case 'design':
        return <CADWorkspace designId={selectedDesignId || undefined} modelUrl={selectedDesignUrl || undefined} />
      case 'printers':
        return <PrinterManagement />
      case 'upload':
        return <DesignUploadPage onBack={() => navigateTo('projects')} projectId={selectedProjectId || undefined} />
      case 'text-to-3d':
        return <TextTo3DPage onBack={() => navigateTo('dashboard')} projectId={selectedProjectId || undefined} />
      case 'print':
        return <PrintDashboard />
      case 'marketplace':
        return <Marketplace />
      case 'settings':
        return <SettingsView />
      default:
        return <Dashboard onNavigate={navigateTo} />
    }
  }

  return (
    <div className="flex h-screen bg-surface-950 overflow-hidden">
      <Sidebar currentView={currentView} onViewChange={navigateTo} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          currentView={currentView} 
          onToggleCopilot={toggleCopilot}
          isCopilotOpen={isCopilotOpen}
          onNavigate={navigateTo}
        />
        
        <main className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {renderView()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <AnimatePresence>
        {isCopilotOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-l border-surface-800 overflow-hidden"
          >
            <AICopilot onClose={toggleCopilot} onInsertModel={handleInsertModel} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
